import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileText, Loader2, Printer } from "lucide-react";
import { api, type AccountingDataset, type AccountingJournalEntry, type AccountingQuery, type DownloadedFile } from "@/api";
import { AccountingExports } from "@/components/accounting-exports";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Input } from "@osmix/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";

/**
 * «Финансы → Бухгалтерия» (поток 1С, встреча 25.09.2026).
 *
 * Всё, что бухгалтер площадки выгружает в 1С и отправляет продавцам: реестр
 * продаж, возвраты, отчёты агента (xlsx и печатная форма), вознаграждение
 * площадки — основа актов и счетов-фактур, взаиморасчёты с продавцами, плюс
 * уже существующие реестр платёжек для 1С и ДДС. Период и продавец — один
 * фильтр на весь раздел. Числа на экране — ровно те, что уйдут в файл:
 * считает их шлюз (`services/bookkeeping.ts`).
 */

const mskToday = (): string => new Date(Date.now() + 3 * 3_600_000).toISOString().slice(0, 10);
const previousMonth = (): string => {
  const [y, m] = mskToday().slice(0, 7).split("-").map(Number);
  const d = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const monthBounds = (period: string): {
  from: string;
  to: string;
} => {
  const [y, m] = period.split("-").map(Number);
  const last = new Date(Date.UTC(y ?? 1970, m ?? 1, 0)).getUTCDate();
  return {
    from: `${period}-01`,
    to: `${period}-${String(last).padStart(2, "0")}`
  };
};
const monthLabel = (period: string) => {
  const [y, m] = period.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, 1)).toLocaleDateString("ru-RU", {
    month: "long",
    year: "numeric"
  });
};

/** Деньги в бухгалтерии — с копейками: «12 345,67 ₽». */
const rub = (value: number) => `${value.toLocaleString("ru-RU", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})} ₽`;
const saveFile = (file: DownloadedFile) => {
  const url = URL.createObjectURL(file.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/** Печатная форма открывается во вкладке: оттуда — «Сохранить в PDF». */
const openPrintable = (file: DownloadedFile) => {
  const url = URL.createObjectURL(new Blob([file.blob], {
    type: "text/html;charset=utf-8"
  }));
  const tab = window.open(url, "_blank");
  if (!tab) saveFile(file);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};
const DATASET_RU: Record<string, string> = {
  "acc-sales": "Реестр продаж",
  "acc-returns": "Возвраты",
  "acc-agent-report": "Отчёт агента",
  "acc-commission-acts": "Вознаграждение (акты)",
  "acc-settlements": "Взаиморасчёты",
  "acc-seller-agent-report": "Отчёт агента (продавец)",
  "acc-seller-sales": "Реестр продаж (продавец)",
  "payout-registry": "Реестр платёжек",
  "cash-flow": "ДДС"
};
type Busy = string | null;
function useExport(canExport: boolean) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (key: string, dataset: AccountingDataset, query: AccountingQuery, format: "xlsx" | "csv" | "html" | "xml") => {
    if (!canExport) return;
    setBusy(key);
    setError(null);
    try {
      const file = await api.finances.exportAccounting(dataset, query, format);
      if (format === "html") openPrintable(file);else saveFile(file);
      await queryClient.invalidateQueries({
        queryKey: ["accounting", "journal"]
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };
  return {
    busy,
    error,
    run
  };
}
function ExportButton(t0) {
  const $ = _c(10);
  const {
    label,
    busy,
    id,
    onClick,
    variant: t1,
    icon: t2,
    disabled
  } = t0;
  const variant = t1 === undefined ? "outline" : t1;
  const icon = t2 === undefined ? "download" : t2;
  const Icon = icon === "print" ? Printer : icon === "file" ? FileText : Download;
  const t3 = busy !== null || disabled;
  let t4;
  if ($[0] !== Icon || $[1] !== busy || $[2] !== id) {
    t4 = busy === id ? <Loader2 className="size-4 animate-spin" /> : <Icon className="size-4" />;
    $[0] = Icon;
    $[1] = busy;
    $[2] = id;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== label || $[5] !== onClick || $[6] !== t3 || $[7] !== t4 || $[8] !== variant) {
    t5 = <Button size="sm" variant={variant} disabled={t3} onClick={onClick}>{t4}{label}</Button>;
    $[4] = label;
    $[5] = onClick;
    $[6] = t3;
    $[7] = t4;
    $[8] = variant;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function Totals(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3 lg:grid-cols-6">{t1}</dl>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(t0) {
  const [label, value] = t0;
  return <div key={label} className="flex flex-col"><dt className="text-xs text-muted-foreground">{label}</dt><dd className="font-semibold tabular-nums">{value}</dd></div>;
}
function State(t0) {
  const $ = _c(6);
  const {
    pending,
    error,
    empty,
    children
  } = t0;
  if (pending) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="text-sm text-muted-foreground">Считаю…</p>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (error) {
    let t1;
    if ($[1] !== error.message) {
      t1 = <p className="text-sm text-destructive">{error.message}</p>;
      $[1] = error.message;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (empty) {
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="text-sm text-muted-foreground">За период данных нет — файл выгрузится пустым, с заголовками.</p>;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== children) {
    t1 = <>{children}</>;
    $[4] = children;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export function AccountingCenter() {
  const $ = _c(296);
  const user = useAuth(_temp2);
  let t0;
  if ($[0] !== user) {
    t0 = hasAdminPermission(user, "action.exports.run");
    $[0] = user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const canExport = t0;
  const [period, setPeriod] = useState(previousMonth);
  const [range, setRange] = useState(_temp3);
  const [shopId, setShopId] = useState("");
  const {
    busy,
    error,
    run
  } = useExport(canExport);
  let t1;
  if ($[2] !== range.from || $[3] !== range.to) {
    t1 = /^\d{4}-\d{2}-\d{2}$/.test(range.from) && /^\d{4}-\d{2}-\d{2}$/.test(range.to) && range.from <= range.to;
    $[2] = range.from;
    $[3] = range.to;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const validRange = t1;
  const t2 = shopId || undefined;
  let t3;
  if ($[5] !== range || $[6] !== t2) {
    t3 = {
      ...range,
      shopId: t2
    };
    $[5] = range;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const dayQuery = t3;
  const t4 = shopId || undefined;
  let t5;
  if ($[8] !== period || $[9] !== t4) {
    t5 = {
      period,
      shopId: t4
    };
    $[8] = period;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const monthQuery = t5;
  const t6 = shopId || undefined;
  let t7;
  if ($[11] !== period || $[12] !== t6) {
    t7 = {
      from: period,
      to: period,
      shopId: t6
    };
    $[11] = period;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const settleQuery = t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      queryKey: ["accounting", "shops"],
      queryFn: _temp4
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const shops = useQuery(t8);
  let t10;
  let t9;
  if ($[15] !== dayQuery) {
    t9 = ["accounting", "sales", dayQuery];
    t10 = () => api.finances.accountingSales(dayQuery);
    $[15] = dayQuery;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t9 || $[20] !== validRange) {
    t11 = {
      queryKey: t9,
      queryFn: t10,
      enabled: validRange
    };
    $[18] = t10;
    $[19] = t9;
    $[20] = validRange;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const sales = useQuery(t11);
  let t12;
  let t13;
  if ($[22] !== dayQuery) {
    t12 = ["accounting", "returns", dayQuery];
    t13 = () => api.finances.accountingReturns(dayQuery);
    $[22] = dayQuery;
    $[23] = t12;
    $[24] = t13;
  } else {
    t12 = $[23];
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t12 || $[26] !== t13 || $[27] !== validRange) {
    t14 = {
      queryKey: t12,
      queryFn: t13,
      enabled: validRange
    };
    $[25] = t12;
    $[26] = t13;
    $[27] = validRange;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const returns = useQuery(t14);
  let t15;
  let t16;
  if ($[29] !== monthQuery) {
    t15 = ["accounting", "agent", monthQuery];
    t16 = () => api.finances.agentReports(monthQuery);
    $[29] = monthQuery;
    $[30] = t15;
    $[31] = t16;
  } else {
    t15 = $[30];
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = /^\d{4}-\d{2}$/;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== period) {
    t18 = t17.test(period);
    $[33] = period;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t15 || $[36] !== t16 || $[37] !== t18) {
    t19 = {
      queryKey: t15,
      queryFn: t16,
      enabled: t18
    };
    $[35] = t15;
    $[36] = t16;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const agent = useQuery(t19);
  let t20;
  let t21;
  if ($[39] !== monthQuery) {
    t20 = ["accounting", "acts", monthQuery];
    t21 = () => api.finances.commissionActs(monthQuery);
    $[39] = monthQuery;
    $[40] = t20;
    $[41] = t21;
  } else {
    t20 = $[40];
    t21 = $[41];
  }
  let t22;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = /^\d{4}-\d{2}$/;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== period) {
    t23 = t22.test(period);
    $[43] = period;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t20 || $[46] !== t21 || $[47] !== t23) {
    t24 = {
      queryKey: t20,
      queryFn: t21,
      enabled: t23
    };
    $[45] = t20;
    $[46] = t21;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  const acts = useQuery(t24);
  let t25;
  let t26;
  if ($[49] !== settleQuery) {
    t25 = ["accounting", "settlements", settleQuery];
    t26 = () => api.finances.settlements(settleQuery);
    $[49] = settleQuery;
    $[50] = t25;
    $[51] = t26;
  } else {
    t25 = $[50];
    t26 = $[51];
  }
  let t27;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = /^\d{4}-\d{2}$/;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== period) {
    t28 = t27.test(period);
    $[53] = period;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== t25 || $[56] !== t26 || $[57] !== t28) {
    t29 = {
      queryKey: t25,
      queryFn: t26,
      enabled: t28
    };
    $[55] = t25;
    $[56] = t26;
    $[57] = t28;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  const settlements = useQuery(t29);
  let t30;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      queryKey: ["accounting", "journal"],
      queryFn: _temp5
    };
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  const journal = useQuery(t30);
  let t31;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = value => {
      if (!/^\d{4}-\d{2}$/.test(value)) {
        return;
      }
      setPeriod(value);
      setRange(monthBounds(value));
    };
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  const changeMonth = t31;
  let T0;
  let T1;
  let T2;
  let T3;
  let t32;
  let t33;
  let t34;
  let t35;
  let t36;
  let t37;
  let t38;
  let t39;
  let t40;
  let t41;
  let t42;
  let t43;
  let t44;
  let t45;
  if ($[61] !== acts.data || $[62] !== acts.error || $[63] !== acts.isPending || $[64] !== agent.data || $[65] !== agent.error || $[66] !== agent.isPending || $[67] !== busy || $[68] !== canExport || $[69] !== dayQuery || $[70] !== error || $[71] !== monthQuery || $[72] !== period || $[73] !== range.from || $[74] !== range.to || $[75] !== returns || $[76] !== run || $[77] !== sales || $[78] !== shopId || $[79] !== shops.data || $[80] !== validRange) {
    const agentRows = agent.data ?? [];
    const agentMissing = agentRows.find(_temp6)?.agentMissing ?? [];
    const actsTotal = (acts.data ?? []).reduce(_temp7, 0);
    const actsVat = (acts.data ?? []).reduce(_temp8, 0);
    t40 = "flex flex-col gap-4";
    t41 = "finances-bookkeeping";
    let t46;
    if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
      t46 = <CardHeader><CardTitle>Бухгалтерия</CardTitle><p className="mt-1 text-sm text-muted-foreground">Выгрузки для 1С и для продавцов по агентскому договору. Даты — по Москве, суммы — в рублях с копейками. Месяц продажи — по дате заказа, как в «Выплатах».</p></CardHeader>;
      $[99] = t46;
    } else {
      t46 = $[99];
    }
    let t47;
    if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
      t47 = e => changeMonth(e.target.value);
      $[100] = t47;
    } else {
      t47 = $[100];
    }
    let t48;
    if ($[101] !== period) {
      t48 = <label className="flex flex-col gap-1 text-xs text-muted-foreground">Отчётный месяц<Input type="month" value={period} onChange={t47} className="h-9 w-44" /></label>;
      $[101] = period;
      $[102] = t48;
    } else {
      t48 = $[102];
    }
    let t49;
    if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
      t49 = e_0 => setRange(r_0 => ({
        ...r_0,
        from: e_0.target.value
      }));
      $[103] = t49;
    } else {
      t49 = $[103];
    }
    let t50;
    if ($[104] !== range.from || $[105] !== range.to) {
      t50 = <label className="flex flex-col gap-1 text-xs text-muted-foreground">Реестры: с<Input type="date" value={range.from} max={range.to} onChange={t49} className="h-9 w-40" /></label>;
      $[104] = range.from;
      $[105] = range.to;
      $[106] = t50;
    } else {
      t50 = $[106];
    }
    let t51;
    if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
      t51 = e_1 => setRange(r_1 => ({
        ...r_1,
        to: e_1.target.value
      }));
      $[107] = t51;
    } else {
      t51 = $[107];
    }
    let t52;
    if ($[108] !== range.from || $[109] !== range.to) {
      t52 = <label className="flex flex-col gap-1 text-xs text-muted-foreground">по<Input type="date" value={range.to} min={range.from} onChange={t51} className="h-9 w-40" /></label>;
      $[108] = range.from;
      $[109] = range.to;
      $[110] = t52;
    } else {
      t52 = $[110];
    }
    let t53;
    if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
      t53 = e_2 => setShopId(e_2.target.value);
      $[111] = t53;
    } else {
      t53 = $[111];
    }
    let t54;
    if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
      t54 = <option value="">Все продавцы</option>;
      $[112] = t54;
    } else {
      t54 = $[112];
    }
    let t55;
    if ($[113] !== shops.data) {
      t55 = shops.data ?? [];
      $[113] = shops.data;
      $[114] = t55;
    } else {
      t55 = $[114];
    }
    let t56;
    if ($[115] !== t55) {
      t56 = t55.map(_temp9);
      $[115] = t55;
      $[116] = t56;
    } else {
      t56 = $[116];
    }
    let t57;
    if ($[117] !== shopId || $[118] !== t56) {
      t57 = <label className="flex flex-col gap-1 text-xs text-muted-foreground">Продавец<Select value={shopId} onChange={t53} className="h-9 w-64">{t54}{t56}</Select></label>;
      $[117] = shopId;
      $[118] = t56;
      $[119] = t57;
    } else {
      t57 = $[119];
    }
    let t58;
    if ($[120] !== canExport) {
      t58 = !canExport && <p className="text-xs text-muted-foreground">Выгрузка файлов требует права «Выгрузки» (action.exports.run).</p>;
      $[120] = canExport;
      $[121] = t58;
    } else {
      t58 = $[121];
    }
    let t59;
    if ($[122] !== t48 || $[123] !== t50 || $[124] !== t52 || $[125] !== t57 || $[126] !== t58) {
      t59 = <CardContent className="flex flex-wrap items-end gap-3">{t48}{t50}{t52}{t57}{t58}</CardContent>;
      $[122] = t48;
      $[123] = t50;
      $[124] = t52;
      $[125] = t57;
      $[126] = t58;
      $[127] = t59;
    } else {
      t59 = $[127];
    }
    let t60;
    if ($[128] !== error) {
      t60 = error && <p className="px-6 pb-4 text-sm text-destructive">{error}</p>;
      $[128] = error;
      $[129] = t60;
    } else {
      t60 = $[129];
    }
    if ($[130] !== t59 || $[131] !== t60) {
      t42 = <Card>{t46}{t59}{t60}</Card>;
      $[130] = t59;
      $[131] = t60;
      $[132] = t42;
    } else {
      t42 = $[132];
    }
    let t61;
    if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
      t61 = <div><CardTitle>Реестр продаж (реализация)</CardTitle><p className="mt-1 text-sm text-muted-foreground">Позиции заказов за период: продавец и ИНН, товар, артикул, кол-во, цена, скидки, сумма, способ оплаты, статус и возвраты. Доставка — отдельной строкой. Покупатель — «Физическое лицо» или организация по счёту, без контактов.</p></div>;
      $[133] = t61;
    } else {
      t61 = $[133];
    }
    let t62;
    if ($[134] !== busy || $[135] !== canExport || $[136] !== dayQuery || $[137] !== run || $[138] !== validRange) {
      t62 = canExport && <div className="flex flex-wrap gap-2"><ExportButton id="sales-xlsx" label={"\u0420\u0435\u0435\u0441\u0442\u0440 (.xlsx)"} variant="default" busy={busy} disabled={!validRange} onClick={() => void run("sales-xlsx", "sales", dayQuery, "xlsx")} /><ExportButton id="sales-csv" label="CSV" busy={busy} disabled={!validRange} onClick={() => void run("sales-csv", "sales", dayQuery, "csv")} /><ExportButton id="sales-xml" label="XML (CommerceML)" icon="file" busy={busy} disabled={!validRange} onClick={() => void run("sales-xml", "sales", dayQuery, "xml")} /></div>;
      $[134] = busy;
      $[135] = canExport;
      $[136] = dayQuery;
      $[137] = run;
      $[138] = validRange;
      $[139] = t62;
    } else {
      t62 = $[139];
    }
    let t63;
    if ($[140] !== t62) {
      t63 = <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">{t61}{t62}</CardHeader>;
      $[140] = t62;
      $[141] = t63;
    } else {
      t63 = $[141];
    }
    let t64;
    if ($[142] !== sales || $[143] !== validRange) {
      t64 = <CardContent>{!validRange ? <p className="text-sm text-destructive">Начало периода позже конца.</p> : <State pending={sales.isPending} error={sales.error} empty={sales.data?.linesTotal === 0}>{sales.data && <Totals items={[["\u0417\u0430\u043A\u0430\u0437\u043E\u0432", String(sales.data.totals.orders)], ["\u0428\u0442\u0443\u043A", String(sales.data.totals.qty)], ["\u0422\u043E\u0432\u0430\u0440\u044B", rub(sales.data.totals.goods)], ["\u0414\u043E\u0441\u0442\u0430\u0432\u043A\u0430 (\u0442\u0440\u0430\u043D\u0437\u0438\u0442)", rub(sales.data.totals.delivery)], ["\u0412\u043E\u0437\u043D\u0430\u0433\u0440\u0430\u0436\u0434\u0435\u043D\u0438\u0435 \u043F\u043B\u043E\u0449\u0430\u0434\u043A\u0438", rub(sales.data.totals.commission)], ["\u0412\u043E\u0437\u0432\u0440\u0430\u0449\u0435\u043D\u043E", rub(sales.data.totals.returned)]]} />}</State>}</CardContent>;
      $[142] = sales;
      $[143] = validRange;
      $[144] = t64;
    } else {
      t64 = $[144];
    }
    if ($[145] !== t63 || $[146] !== t64) {
      t43 = <Card>{t63}{t64}</Card>;
      $[145] = t63;
      $[146] = t64;
      $[147] = t43;
    } else {
      t43 = $[147];
    }
    let t65;
    if ($[148] === Symbol.for("react.memo_cache_sentinel")) {
      t65 = <div><CardTitle>Возвраты за период</CardTitle><p className="mt-1 text-sm text-muted-foreground">Заявки на возврат: деньги вернули — по дате возврата, остальные — по дате заявки. В итог идут только возвраты, по которым деньги ушли покупателю.</p></div>;
      $[148] = t65;
    } else {
      t65 = $[148];
    }
    let t66;
    if ($[149] !== busy || $[150] !== canExport || $[151] !== dayQuery || $[152] !== run || $[153] !== validRange) {
      t66 = canExport && <div className="flex flex-wrap gap-2"><ExportButton id="ret-xlsx" label={"\u0412\u043E\u0437\u0432\u0440\u0430\u0442\u044B (.xlsx)"} variant="default" busy={busy} disabled={!validRange} onClick={() => void run("ret-xlsx", "returns", dayQuery, "xlsx")} /><ExportButton id="ret-csv" label="CSV" busy={busy} disabled={!validRange} onClick={() => void run("ret-csv", "returns", dayQuery, "csv")} /></div>;
      $[149] = busy;
      $[150] = canExport;
      $[151] = dayQuery;
      $[152] = run;
      $[153] = validRange;
      $[154] = t66;
    } else {
      t66 = $[154];
    }
    let t67;
    if ($[155] !== t66) {
      t67 = <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">{t65}{t66}</CardHeader>;
      $[155] = t66;
      $[156] = t67;
    } else {
      t67 = $[156];
    }
    let t68;
    if ($[157] !== returns || $[158] !== validRange) {
      t68 = validRange && <State pending={returns.isPending} error={returns.error} empty={returns.data?.totals.count === 0}>{returns.data && <Totals items={[["\u0417\u0430\u044F\u0432\u043E\u043A", String(returns.data.totals.count)], ["\u0414\u0435\u043D\u044C\u0433\u0438 \u0432\u043E\u0437\u0432\u0440\u0430\u0449\u0435\u043D\u044B", String(returns.data.totals.refunded)], ["\u0422\u043E\u0432\u0430\u0440\u044B", rub(returns.data.totals.goods)], ["\u0414\u043E\u0441\u0442\u0430\u0432\u043A\u0430", rub(returns.data.totals.delivery)], ["\u0418\u0442\u043E\u0433\u043E \u0432\u043E\u0437\u0432\u0440\u0430\u0449\u0435\u043D\u043E", rub(returns.data.totals.total)]]} />}</State>;
      $[157] = returns;
      $[158] = validRange;
      $[159] = t68;
    } else {
      t68 = $[159];
    }
    let t69;
    if ($[160] !== t68) {
      t69 = <CardContent>{t68}</CardContent>;
      $[160] = t68;
      $[161] = t69;
    } else {
      t69 = $[161];
    }
    if ($[162] !== t67 || $[163] !== t69) {
      t44 = <Card>{t67}{t69}</Card>;
      $[162] = t67;
      $[163] = t69;
      $[164] = t44;
    } else {
      t44 = $[164];
    }
    let t70;
    if ($[165] !== period) {
      t70 = monthLabel(period);
      $[165] = period;
      $[166] = t70;
    } else {
      t70 = $[166];
    }
    let t71;
    if ($[167] !== t70) {
      t71 = <CardTitle>Отчёты агента за {t70}</CardTitle>;
      $[167] = t70;
      $[168] = t71;
    } else {
      t71 = $[168];
    }
    let t72;
    if ($[169] === Symbol.for("react.memo_cache_sentinel")) {
      t72 = <p className="mt-1 text-sm text-muted-foreground">Отчёт площадки продавцу по агентскому договору (ст. 1008 ГК): получено от покупателей, возвраты, вознаграждение, удержания, к перечислению, перечислено, остатки. Сводная и построчная часть; печатная форма — для отправки продавцу.</p>;
      $[169] = t72;
    } else {
      t72 = $[169];
    }
    let t73;
    if ($[170] !== t71) {
      t73 = <div>{t71}{t72}</div>;
      $[170] = t71;
      $[171] = t73;
    } else {
      t73 = $[171];
    }
    let t74;
    if ($[172] !== busy || $[173] !== canExport || $[174] !== monthQuery || $[175] !== run || $[176] !== shopId) {
      t74 = canExport && <div className="flex flex-wrap gap-2"><ExportButton id="agent-xlsx" label={shopId ? "\u041E\u0442\u0447\u0451\u0442 (.xlsx)" : "\u0421\u0432\u043E\u0434\u043D\u044B\u0439 (.xlsx)"} variant="default" busy={busy} onClick={() => void run("agent-xlsx", "agent-reports", monthQuery, "xlsx")} /><ExportButton id="agent-html" label={shopId ? "\u041F\u0435\u0447\u0430\u0442\u043D\u0430\u044F \u0444\u043E\u0440\u043C\u0430 (PDF)" : "\u0412\u0441\u0435 \u043F\u0435\u0447\u0430\u0442\u043D\u044B\u0435 \u0444\u043E\u0440\u043C\u044B (PDF)"} icon="print" busy={busy} onClick={() => void run("agent-html", "agent-reports", monthQuery, "html")} /><ExportButton id="agent-xml" label="XML (CommerceML)" icon="file" busy={busy} onClick={() => void run("agent-xml", "agent-reports", monthQuery, "xml")} /></div>;
      $[172] = busy;
      $[173] = canExport;
      $[174] = monthQuery;
      $[175] = run;
      $[176] = shopId;
      $[177] = t74;
    } else {
      t74 = $[177];
    }
    let t75;
    if ($[178] !== t73 || $[179] !== t74) {
      t75 = <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">{t73}{t74}</CardHeader>;
      $[178] = t73;
      $[179] = t74;
      $[180] = t75;
    } else {
      t75 = $[180];
    }
    const t76 = agentMissing.length > 0 && <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300">В отчёте нет реквизитов агента (площадки): не заданы {agentMissing.join(", ")}. Их задают в окружении шлюза — до этого в шапке отчёта стоит пометка «реквизиты не заданы».</p>;
    const t77 = agent.isPending;
    const t78 = agent.error;
    const t79 = agentRows.length === 0;
    let t80;
    let t81;
    let t82;
    let t83;
    let t84;
    let t85;
    let t86;
    let t87;
    if ($[181] === Symbol.for("react.memo_cache_sentinel")) {
      t80 = <TableHead>Продавец</TableHead>;
      t81 = <TableHead className="text-right">Продажи</TableHead>;
      t82 = <TableHead className="text-right">Возвраты</TableHead>;
      t83 = <TableHead className="text-right">Вознаграждение</TableHead>;
      t84 = <TableHead className="text-right">Начислено</TableHead>;
      t85 = <TableHead className="text-right">Перечислено</TableHead>;
      t86 = <TableHead className="text-right">Осталось</TableHead>;
      t87 = <TableHead className="text-right">Остаток на конец</TableHead>;
      $[181] = t80;
      $[182] = t81;
      $[183] = t82;
      $[184] = t83;
      $[185] = t84;
      $[186] = t85;
      $[187] = t86;
      $[188] = t87;
    } else {
      t80 = $[181];
      t81 = $[182];
      t82 = $[183];
      t83 = $[184];
      t84 = $[185];
      t85 = $[186];
      t86 = $[187];
      t87 = $[188];
    }
    let t88;
    if ($[189] !== canExport) {
      t88 = canExport && <TableHead />;
      $[189] = canExport;
      $[190] = t88;
    } else {
      t88 = $[190];
    }
    let t89;
    if ($[191] !== t88) {
      t89 = <TableHeader><TableRow>{t80}{t81}{t82}{t83}{t84}{t85}{t86}{t87}{t88}</TableRow></TableHeader>;
      $[191] = t88;
      $[192] = t89;
    } else {
      t89 = $[192];
    }
    let t90;
    if ($[193] !== agentRows || $[194] !== busy || $[195] !== canExport || $[196] !== period || $[197] !== run) {
      let t91;
      if ($[199] !== busy || $[200] !== canExport || $[201] !== period || $[202] !== run) {
        t91 = r_2 => <TableRow key={r_2.number}><TableCell><div className="font-medium">{r_2.seller.shopName}</div><div className="text-xs text-muted-foreground">{r_2.seller.legalName} · ИНН {r_2.seller.inn || "\u043D\u0435 \u0443\u043A\u0430\u0437\u0430\u043D"} · {r_2.seller.vat}</div></TableCell><TableCell className="text-right tabular-nums">{rub(r_2.summary.sales)}</TableCell><TableCell className="text-right tabular-nums">{rub(r_2.summary.refunds)}</TableCell><TableCell className="text-right tabular-nums">{rub(r_2.summary.commission)}</TableCell><TableCell className="text-right font-semibold tabular-nums">{rub(r_2.summary.accrued)}</TableCell><TableCell className="text-right tabular-nums">{rub(r_2.summary.paidForPeriod)}</TableCell><TableCell className="text-right tabular-nums">{rub(r_2.summary.due)}</TableCell><TableCell className="text-right tabular-nums">{rub(r_2.summary.closing)}</TableCell>{canExport && <TableCell><div className="flex justify-end gap-1"><ExportButton id={`a-x-${r_2.seller.shopId}`} label="xlsx" busy={busy} onClick={() => void run(`a-x-${r_2.seller.shopId}`, "agent-reports", {
                period,
                shopId: r_2.seller.shopId
              }, "xlsx")} /><ExportButton id={`a-h-${r_2.seller.shopId}`} label="PDF" icon="print" busy={busy} onClick={() => void run(`a-h-${r_2.seller.shopId}`, "agent-reports", {
                period,
                shopId: r_2.seller.shopId
              }, "html")} /></div></TableCell>}</TableRow>;
        $[199] = busy;
        $[200] = canExport;
        $[201] = period;
        $[202] = run;
        $[203] = t91;
      } else {
        t91 = $[203];
      }
      t90 = agentRows.map(t91);
      $[193] = agentRows;
      $[194] = busy;
      $[195] = canExport;
      $[196] = period;
      $[197] = run;
      $[198] = t90;
    } else {
      t90 = $[198];
    }
    let t91;
    if ($[204] !== t90) {
      t91 = <TableBody>{t90}</TableBody>;
      $[204] = t90;
      $[205] = t91;
    } else {
      t91 = $[205];
    }
    let t92;
    if ($[206] !== t89 || $[207] !== t91) {
      t92 = <div className="overflow-x-auto"><Table>{t89}{t91}</Table></div>;
      $[206] = t89;
      $[207] = t91;
      $[208] = t92;
    } else {
      t92 = $[208];
    }
    let t93;
    if ($[209] !== agent.error || $[210] !== agent.isPending || $[211] !== t79 || $[212] !== t92) {
      t93 = <State pending={t77} error={t78} empty={t79}>{t92}</State>;
      $[209] = agent.error;
      $[210] = agent.isPending;
      $[211] = t79;
      $[212] = t92;
      $[213] = t93;
    } else {
      t93 = $[213];
    }
    let t94;
    if ($[214] !== t76 || $[215] !== t93) {
      t94 = <CardContent className="flex flex-col gap-3">{t76}{t93}</CardContent>;
      $[214] = t76;
      $[215] = t93;
      $[216] = t94;
    } else {
      t94 = $[216];
    }
    if ($[217] !== t75 || $[218] !== t94) {
      t45 = <Card>{t75}{t94}</Card>;
      $[217] = t75;
      $[218] = t94;
      $[219] = t45;
    } else {
      t45 = $[219];
    }
    T3 = Card;
    let t95;
    if ($[220] === Symbol.for("react.memo_cache_sentinel")) {
      t95 = <div><CardTitle>Вознаграждение площадки (основа актов и счетов-фактур)</CardTitle><p className="mt-1 text-sm text-muted-foreground">Комиссия по каждому продавцу за месяц — по тем же ставкам, что в «Комиссиях». В файле: лист начислений, лист под табличную часть «Услуги» документа «Реализация (акты, накладные, УПД)» и лист под справочник «Контрагенты» для загрузки в 1С.</p></div>;
      $[220] = t95;
    } else {
      t95 = $[220];
    }
    let t96;
    if ($[221] !== busy || $[222] !== canExport || $[223] !== monthQuery || $[224] !== run) {
      t96 = canExport && <ExportButton id="acts-xlsx" label={"\u041D\u0430\u0447\u0438\u0441\u043B\u0435\u043D\u0438\u044F (.xlsx)"} variant="default" busy={busy} onClick={() => void run("acts-xlsx", "commission-acts", monthQuery, "xlsx")} />;
      $[221] = busy;
      $[222] = canExport;
      $[223] = monthQuery;
      $[224] = run;
      $[225] = t96;
    } else {
      t96 = $[225];
    }
    if ($[226] !== t96) {
      t39 = <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">{t95}{t96}</CardHeader>;
      $[226] = t96;
      $[227] = t39;
    } else {
      t39 = $[227];
    }
    T2 = CardContent;
    T1 = State;
    t36 = acts.isPending;
    t37 = acts.error;
    t38 = (acts.data ?? []).length === 0;
    T0 = Totals;
    const t97 = String(acts.data?.length ?? 0);
    if ($[228] !== t97) {
      t34 = ["\u041F\u0440\u043E\u0434\u0430\u0432\u0446\u043E\u0432", t97];
      $[228] = t97;
      $[229] = t34;
    } else {
      t34 = $[229];
    }
    const t98 = rub(actsTotal);
    if ($[230] !== t98) {
      t35 = ["\u0412\u043E\u0437\u043D\u0430\u0433\u0440\u0430\u0436\u0434\u0435\u043D\u0438\u0435", t98];
      $[230] = t98;
      $[231] = t35;
    } else {
      t35 = $[231];
    }
    t32 = "\u041D\u0414\u0421";
    t33 = acts.data?.[0]?.vatLabel === "\u0411\u0435\u0437 \u041D\u0414\u0421" ? "\u0411\u0435\u0437 \u041D\u0414\u0421" : rub(actsVat);
    $[61] = acts.data;
    $[62] = acts.error;
    $[63] = acts.isPending;
    $[64] = agent.data;
    $[65] = agent.error;
    $[66] = agent.isPending;
    $[67] = busy;
    $[68] = canExport;
    $[69] = dayQuery;
    $[70] = error;
    $[71] = monthQuery;
    $[72] = period;
    $[73] = range.from;
    $[74] = range.to;
    $[75] = returns;
    $[76] = run;
    $[77] = sales;
    $[78] = shopId;
    $[79] = shops.data;
    $[80] = validRange;
    $[81] = T0;
    $[82] = T1;
    $[83] = T2;
    $[84] = T3;
    $[85] = t32;
    $[86] = t33;
    $[87] = t34;
    $[88] = t35;
    $[89] = t36;
    $[90] = t37;
    $[91] = t38;
    $[92] = t39;
    $[93] = t40;
    $[94] = t41;
    $[95] = t42;
    $[96] = t43;
    $[97] = t44;
    $[98] = t45;
  } else {
    T0 = $[81];
    T1 = $[82];
    T2 = $[83];
    T3 = $[84];
    t32 = $[85];
    t33 = $[86];
    t34 = $[87];
    t35 = $[88];
    t36 = $[89];
    t37 = $[90];
    t38 = $[91];
    t39 = $[92];
    t40 = $[93];
    t41 = $[94];
    t42 = $[95];
    t43 = $[96];
    t44 = $[97];
    t45 = $[98];
  }
  let t46;
  if ($[232] !== t32 || $[233] !== t33) {
    t46 = [t32, t33];
    $[232] = t32;
    $[233] = t33;
    $[234] = t46;
  } else {
    t46 = $[234];
  }
  let t47;
  if ($[235] !== t34 || $[236] !== t35 || $[237] !== t46) {
    t47 = [t34, t35, t46];
    $[235] = t34;
    $[236] = t35;
    $[237] = t46;
    $[238] = t47;
  } else {
    t47 = $[238];
  }
  let t48;
  if ($[239] !== T0 || $[240] !== t47) {
    t48 = <T0 items={t47} />;
    $[239] = T0;
    $[240] = t47;
    $[241] = t48;
  } else {
    t48 = $[241];
  }
  let t49;
  if ($[242] !== T1 || $[243] !== t36 || $[244] !== t37 || $[245] !== t38 || $[246] !== t48) {
    t49 = <T1 pending={t36} error={t37} empty={t38}>{t48}</T1>;
    $[242] = T1;
    $[243] = t36;
    $[244] = t37;
    $[245] = t38;
    $[246] = t48;
    $[247] = t49;
  } else {
    t49 = $[247];
  }
  let t50;
  if ($[248] !== T2 || $[249] !== t49) {
    t50 = <T2>{t49}</T2>;
    $[248] = T2;
    $[249] = t49;
    $[250] = t50;
  } else {
    t50 = $[250];
  }
  let t51;
  if ($[251] !== T3 || $[252] !== t39 || $[253] !== t50) {
    t51 = <T3>{t39}{t50}</T3>;
    $[251] = T3;
    $[252] = t39;
    $[253] = t50;
    $[254] = t51;
  } else {
    t51 = $[254];
  }
  let t52;
  if ($[255] !== period) {
    t52 = monthLabel(period);
    $[255] = period;
    $[256] = t52;
  } else {
    t52 = $[256];
  }
  let t53;
  if ($[257] !== t52) {
    t53 = <CardTitle>Взаиморасчёты с продавцами за {t52}</CardTitle>;
    $[257] = t52;
    $[258] = t53;
  } else {
    t53 = $[258];
  }
  let t54;
  if ($[259] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <p className="mt-1 text-sm text-muted-foreground">Сальдо на начало, начислено, выплачено, сальдо на конец — долг площадки перед продавцом. Начисление — по месяцу заказа, выплата — по дате перечисления.</p>;
    $[259] = t54;
  } else {
    t54 = $[259];
  }
  let t55;
  if ($[260] !== t53) {
    t55 = <div>{t53}{t54}</div>;
    $[260] = t53;
    $[261] = t55;
  } else {
    t55 = $[261];
  }
  let t56;
  if ($[262] !== busy || $[263] !== canExport || $[264] !== run || $[265] !== settleQuery) {
    t56 = canExport && <ExportButton id="set-xlsx" label={"\u0412\u0437\u0430\u0438\u043C\u043E\u0440\u0430\u0441\u0447\u0451\u0442\u044B (.xlsx)"} variant="default" busy={busy} onClick={() => void run("set-xlsx", "settlements", settleQuery, "xlsx")} />;
    $[262] = busy;
    $[263] = canExport;
    $[264] = run;
    $[265] = settleQuery;
    $[266] = t56;
  } else {
    t56 = $[266];
  }
  let t57;
  if ($[267] !== t55 || $[268] !== t56) {
    t57 = <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">{t55}{t56}</CardHeader>;
    $[267] = t55;
    $[268] = t56;
    $[269] = t57;
  } else {
    t57 = $[269];
  }
  const t58 = (settlements.data?.rows.length ?? 0) === 0;
  let t59;
  if ($[270] !== settlements.data) {
    t59 = settlements.data && <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Продавец</TableHead><TableHead className="text-right">Сальдо на начало</TableHead><TableHead className="text-right">Начислено</TableHead><TableHead className="text-right">Выплачено</TableHead><TableHead className="text-right">Сальдо на конец</TableHead><TableHead className="text-right">В заявках</TableHead></TableRow></TableHeader><TableBody>{settlements.data.rows.map(_temp0)}<TableRow className="font-semibold"><TableCell>Итого</TableCell><TableCell className="text-right tabular-nums">{rub(settlements.data.totals.opening)}</TableCell><TableCell className="text-right tabular-nums">{rub(settlements.data.totals.accrued)}</TableCell><TableCell className="text-right tabular-nums">{rub(settlements.data.totals.paid)}</TableCell><TableCell className="text-right tabular-nums">{rub(settlements.data.totals.closing)}</TableCell><TableCell className="text-right tabular-nums">{rub(settlements.data.totals.pending)}</TableCell></TableRow></TableBody></Table></div>;
    $[270] = settlements.data;
    $[271] = t59;
  } else {
    t59 = $[271];
  }
  let t60;
  if ($[272] !== settlements.error || $[273] !== settlements.isPending || $[274] !== t58 || $[275] !== t59) {
    t60 = <CardContent><State pending={settlements.isPending} error={settlements.error} empty={t58}>{t59}</State></CardContent>;
    $[272] = settlements.error;
    $[273] = settlements.isPending;
    $[274] = t58;
    $[275] = t59;
    $[276] = t60;
  } else {
    t60 = $[276];
  }
  let t61;
  if ($[277] !== t57 || $[278] !== t60) {
    t61 = <Card>{t57}{t60}</Card>;
    $[277] = t57;
    $[278] = t60;
    $[279] = t61;
  } else {
    t61 = $[279];
  }
  let t62;
  if ($[280] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = <AccountingExports />;
    $[280] = t62;
  } else {
    t62 = $[280];
  }
  let t63;
  if ($[281] !== journal.data) {
    t63 = journal.data ?? [];
    $[281] = journal.data;
    $[282] = t63;
  } else {
    t63 = $[282];
  }
  let t64;
  if ($[283] !== journal.isPending || $[284] !== t63) {
    t64 = <JournalCard entries={t63} pending={journal.isPending} />;
    $[283] = journal.isPending;
    $[284] = t63;
    $[285] = t64;
  } else {
    t64 = $[285];
  }
  let t65;
  if ($[286] !== t40 || $[287] !== t41 || $[288] !== t42 || $[289] !== t43 || $[290] !== t44 || $[291] !== t45 || $[292] !== t51 || $[293] !== t61 || $[294] !== t64) {
    t65 = <div className={t40} data-tour={t41}>{t42}{t43}{t44}{t45}{t51}{t61}{t62}{t64}</div>;
    $[286] = t40;
    $[287] = t41;
    $[288] = t42;
    $[289] = t43;
    $[290] = t44;
    $[291] = t45;
    $[292] = t51;
    $[293] = t61;
    $[294] = t64;
    $[295] = t65;
  } else {
    t65 = $[295];
  }
  return t65;
}
function _temp0(r_3) {
  return <TableRow key={r_3.shopId}><TableCell><div className="font-medium">{r_3.seller.shopName}</div><div className="text-xs text-muted-foreground">ИНН {r_3.seller.inn || "\u043D\u0435 \u0443\u043A\u0430\u0437\u0430\u043D"}</div></TableCell><TableCell className="text-right tabular-nums">{rub(r_3.opening)}</TableCell><TableCell className="text-right tabular-nums">{rub(r_3.accrued)}</TableCell><TableCell className="text-right tabular-nums">{rub(r_3.paid)}</TableCell><TableCell className="text-right font-semibold tabular-nums">{rub(r_3.closing)}</TableCell><TableCell className="text-right tabular-nums text-muted-foreground">{rub(r_3.pending)}</TableCell></TableRow>;
}
function _temp9(s_1) {
  return <option key={s_1.shopId} value={s_1.shopId}>{s_1.name}</option>;
}
function _temp8(s_0, a_0) {
  return s_0 + a_0.vat;
}
function _temp7(s, a) {
  return s + a.commission;
}
function _temp6(r) {
  return r.agentMissing.length > 0;
}
function _temp5() {
  return api.finances.accountingJournal();
}
function _temp4() {
  return api.finances.accountingShops();
}
function _temp3() {
  return monthBounds(previousMonth());
}
function _temp2(st) {
  return st.user;
}
const filterText = (f: Record<string, unknown>) => {
  const parts: string[] = [];
  if (typeof f.period === "string") parts.push(f.period.split("-").reverse().join("."));
  if (typeof f.from === "string" && typeof f.to === "string") {
    const d = (v: string) => v.split("-").reverse().join(".");
    parts.push(`${d(f.from)} – ${d(f.to)}`);
  }
  if (typeof f.shopId === "string") parts.push(f.shopId);
  return parts.join(" · ");
};
function JournalCard(t0) {
  const $ = _c(4);
  const {
    entries,
    pending
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CardHeader><CardTitle>Журнал выгрузок</CardTitle><p className="mt-1 text-sm text-muted-foreground">Кто, что и за какой период выгружал — последние 30 выгрузок.</p></CardHeader>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== entries || $[2] !== pending) {
    t2 = <Card>{t1}<CardContent>{pending ? <p className="text-sm text-muted-foreground">Загрузка…</p> : entries.length === 0 ? <p className="text-sm text-muted-foreground">Выгрузок ещё не было.</p> : <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Когда</TableHead><TableHead>Выгрузка</TableHead><TableHead>Период / продавец</TableHead><TableHead>Формат</TableHead><TableHead className="text-right">Строк</TableHead><TableHead>Кто</TableHead></TableRow></TableHeader><TableBody>{entries.map(_temp1)}</TableBody></Table></div>}</CardContent></Card>;
    $[1] = entries;
    $[2] = pending;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp1(e) {
  return <TableRow key={e.id}><TableCell className="text-sm tabular-nums">{new Date(e.createdAt).toLocaleString("ru-RU", {
        timeZone: "Europe/Moscow",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })}</TableCell><TableCell className="text-sm">{DATASET_RU[e.dataset] ?? e.dataset}</TableCell><TableCell className="text-xs text-muted-foreground">{filterText(e.filters)}</TableCell><TableCell className="text-xs uppercase">{e.format === "html" ? "PDF" : e.format === "xml" ? "XML" : e.format}</TableCell><TableCell className="text-right tabular-nums">{e.rows}</TableCell><TableCell className="text-sm">{e.who}</TableCell></TableRow>;
}